import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { useNavigate } from 'react-router-dom'
import AssignmentIcon from '@mui/icons-material/Assignment'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ContactPageIcon from '@mui/icons-material/ContactPage'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import PaidIcon from '@mui/icons-material/Paid'
import PeopleIcon from '@mui/icons-material/People'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import SettingsIcon from '@mui/icons-material/Settings'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import {
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import IconButton from '@mui/material/IconButton'
import { styled, useTheme } from '@mui/material/styles'

import { ErrorBoundary } from '../../containers/ErrorBoundary'
import { BaseRouter } from '../../router/BaseRouter'
import { Path } from '../../router/BaseRouter/enums'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { onLogout } from '../../store/rootActions'
import { isAdminSelector } from '../../store/slices/roleSlice'
import { isSidebarOpenSelector, userOptionsActions } from '../../store/slices/userOptionsSlice'
import { Header } from '../Header'
import { LSToastContainer } from '../LsToast'

import 'react-toastify/dist/ReactToastify.css'

const drawerWidth = 280

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}))

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: 'none',
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 0),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}))

export const Layout: FC = () => {
  const theme = useTheme()
  const open = useAppSelector(isSidebarOpenSelector)
  const isAdmin = useAppSelector(isAdminSelector)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const goTo = (path: string) => {
    navigate(path)
  }

  const toggleDrawer = () => {
    dispatch(userOptionsActions.toggleSidebar())
  }

  const auth = useAuth()

  const logout = () => {
    dispatch(onLogout())
    void auth.signoutRedirect({ id_token_hint: auth.user?.id_token })
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Header />
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={toggleDrawer}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          <Typography
            variant="overline"
            sx={{
              px: 2,
              pt: 2,
              pb: 0.5,
              display: 'block',
              color: 'text.secondary',
            }}
          >
            {t('menu.planning')}
          </Typography>
          <ListItem disablePadding>
            <ListItemButton onClick={() => goTo(Path.DAY_PLAN)}>
              <ListItemIcon>
                <CalendarMonthIcon />
              </ListItemIcon>
              <ListItemText primary={t('dayPlan.title')} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => goTo(Path.REQUESTS)}>
              <ListItemIcon>
                <AssignmentIcon />
              </ListItemIcon>
              <ListItemText primary={t('requests.title')} />
            </ListItemButton>
          </ListItem>
        </List>
        <List>
          <Typography
            variant="overline"
            sx={{
              px: 2,
              pt: 1,
              pb: 0.5,
              display: 'block',
              color: 'text.secondary',
            }}
          >
            {t('menu.pokladna')}
          </Typography>
          <ListItem disablePadding>
            <ListItemButton onClick={() => goTo(Path.RECEIPTS)}>
              <ListItemIcon>
                <ReceiptLongIcon />
              </ListItemIcon>
              <ListItemText primary={t('receipts.title')} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => goTo(Path.POS_CLOSINGS)}>
              <ListItemIcon>
                <PointOfSaleIcon />
              </ListItemIcon>
              <ListItemText primary={t('posClosings.title')} />
            </ListItemButton>
          </ListItem>
        </List>
        <List>
          <Typography
            variant="overline"
            sx={{
              px: 2,
              pt: 1,
              pb: 0.5,
              display: 'block',
              color: 'text.secondary',
            }}
          >
            {t('menu.employees')}
          </Typography>
          <ListItem disablePadding>
            <ListItemButton onClick={() => goTo(Path.INSTRUCTORS)}>
              <ListItemIcon>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText primary={t('instructors.title')} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => goTo(Path.INSTRUCTOR_AVAILABILITY)}>
              <ListItemIcon>
                <EventAvailableIcon />
              </ListItemIcon>
              <ListItemText primary={t('availability.title')} />
            </ListItemButton>
          </ListItem>
          {isAdmin && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => goTo(Path.INSTRUCTOR_SALARY)}>
                <ListItemIcon>
                  <PaidIcon />
                </ListItemIcon>
                <ListItemText primary={t('instructorSalaryOverview.title')} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
        <List>
          <Typography
            variant="overline"
            sx={{
              px: 2,
              pt: 1,
              pb: 0.5,
              display: 'block',
              color: 'text.secondary',
            }}
          >
            {t('menu.catalog')}
          </Typography>
          <ListItem disablePadding>
            <ListItemButton onClick={() => goTo(Path.CUSTOMERS)}>
              <ListItemIcon>
                <ContactPageIcon />
              </ListItemIcon>
              <ListItemText primary={t('customers.title')} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => goTo(Path.PRODUCTS)}>
              <ListItemIcon>
                <ShoppingCartIcon />
              </ListItemIcon>
              <ListItemText primary={t('products.title')} />
            </ListItemButton>
          </ListItem>
        </List>
        <Divider />
        <List>
          {isAdmin && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => goTo(Path.SETTINGS)}>
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary={t('settings.title')} />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton onClick={logout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t('logout')} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        <LSToastContainer />
        <Box>
          <ErrorBoundary>
            <BaseRouter />
          </ErrorBoundary>
        </Box>
      </Main>
    </Box>
  )
}
